import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useErrorNotification } from '../../hooks/useErrorNotification';
import { type AdminUser, type StatusMessage, type UsersListResponse } from './types';
import { formatDateTime } from './utils';
import { UserDrawer } from './users/UserDrawer';

export function UsersManagement() {
  const { authedRequest } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  useErrorNotification(status, { header: 'User management error' });

  const loadUsers = useCallback(
    async (nextPage: number) => {
      setLoading(true);
      setStatus(null);
      try {
        const data = await authedRequest<UsersListResponse>(
          `/api/admin/users?page=${nextPage}&pageSize=${pageSize}`
        );
        setUsers(data.items);
        setTotalItems(data.totalItems);
      } catch (err: any) {
        setStatus({ type: 'err', text: err?.message || 'Failed to load users.' });
      } finally {
        setLoading(false);
      }
    },
    [authedRequest, pageSize]
  );

  useEffect(() => {
    loadUsers(page);
  }, [loadUsers, page]);

  const handleUserUpdated = useCallback((next: AdminUser) => {
    setUsers((prev) => prev.map((user) => (user.id === next.id ? next : user)));
  }, []);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize]);

  return (
    <div className="card card-outline card-secondary">
      <div className="card-header border-0">
        <h3 className="card-title mb-0">
          <i className="fas fa-users-cog me-2" aria-hidden="true" /> Users Management
        </h3>
        <p className="text-muted mb-0">Review accounts and manage access.</p>
      </div>
      <div className="card-body">
        {status && status.type !== 'err' && (
          <div
            className={`alert alert-${status.type === 'ok' ? 'success' : 'info'} alert-dismissible fade show`}
            role="status"
          >
            {status.text}
            <button type="button" className="btn-close" onClick={() => setStatus(null)} aria-label="Dismiss" />
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Signed up</th>
                <th>Status</th>
                <th>Last login</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((userRow) => (
                <tr key={userRow.id}>
                  <td className="text-break">{userRow.email}</td>
                  <td>{userRow.name || 'N/A'}</td>
                  <td>{formatDateTime(userRow.signUpAt)}</td>
                  <td>
                    <span className={`badge ${userRow.isActive ? 'bg-success' : 'bg-secondary'}`}>
                      {userRow.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatDateTime(userRow.lastLoginAt)}</td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => setSelectedUser(userRow)}
                      disabled={loading}
                    >
                      <i className="fas fa-cog me-1" aria-hidden="true" /> Manage
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && !loading ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No users found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {loading && (
          <div className="text-center text-muted small">Loading users…</div>
        )}
      </div>
      <div className="card-footer d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
        <div className="text-muted small">
          Showing page {page} of {totalPages} ({totalItems} users)
        </div>
        <div className="btn-group" role="group" aria-label="Users pagination">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setPage(1)}
            disabled={page === 1 || loading}
          >
            « First
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1 || loading}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page === totalPages || loading}
          >
            Next ›
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages || loading}
          >
            Last »
          </button>
        </div>
      </div>

      {selectedUser && (
        <UserDrawer
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </div>
  );
}
